import { endpoints } from "../../../api/endpoint";
import { FINANCIER_TYPES } from "../../preOnboarding/constants/documents.config";
import { getFirebaseTimestampString } from "../../utils";
import {
  FileText,
  CheckCircle2,
  FileEdit,
  Clock,
  PauseCircle,
  Edit3,
  ShieldCheck,
  XCircle,
  Trash2,
} from "lucide-react";

export const TABLE_TYPES = {
  PRE_ONBOARDING: "pre-onboarding",
  ONBOARDED: "onboarded",
  LEADS: "leads",
};

export const ONBOARDING_STATUS = {
  PENDING: "PENDING",
  HOLD: "HOLD",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  DELETED: "DELETED",
  DOC_CHANGE_REQUESTED: "DOC_CHANGE_REQUESTED",
};

export const STATUS_OPTIONS = [
  { label: "Pending", value: "Pending", color: "default" },
  { label: "Approved", value: "Approved", color: "success" },
  { label: "Hold", value: "Hold", color: "warning" },
  {
    label: "Documents Change Requested",
    value: ONBOARDING_STATUS.DOC_CHANGE_REQUESTED,
    color: "warning",
  },
  { label: "Rejected", value: "Rejected", color: "error" },
];

const financierOptions = Object.values(FINANCIER_TYPES).map((type) => ({
  label: type,
  value: type,
}));

export const PRE_ONBOARDING_CONFIG = {
  id: TABLE_TYPES.PRE_ONBOARDING,
  title: "Pre-Onboarding",
  apiEndpoint: endpoints.getPreOnboardingDriver,
  columns: [
    { id: "prn", label: "PRN", width: "120px", whiteSpace: "nowrap" },
    { id: "driverName", label: "Driver Name", width: "130px" },
    { id: "driverId", label: "Driver ID", width: "180px" },
    { id: "mobileNumber", label: "Mobile", width: "130px" },
    { id: "financierType", label: "Financier", width: "130px" },
    { id: "status", label: "Status", width: "120px" },
    { id: "leadId", label: "Lead Id", width: "120px", whiteSpace: "nowrap" },
    {
      id: "dealerId",
      label: "Dealer Id",
      width: "120px",
      whiteSpace: "nowrap",
    },
    { id: "createdAt", label: "Date", width: "120px", whiteSpace: "nowrap" },
    { id: "action", label: "Action", width: "70px", align: "center" },
  ],
  filters: [
    {
      id: "status",
      label: "Status",
      type: "select",
      options: STATUS_OPTIONS,
    },
    {
      id: "financierType",
      label: "Financier Type",
      type: "select",
      options: financierOptions,
    },
  ],
  stats: {
    type: "flat",
    source: "statusCounts",
    items: [
      {
        label: "Pending",
        key: ONBOARDING_STATUS.PENDING,
        color: "#8B8B8B",
        icon: Clock,
      },
      {
        label: "On hold",
        key: ONBOARDING_STATUS.HOLD,
        color: "#D69A1F",
        icon: PauseCircle,
      },
      {
        label: "Approved",
        key: ONBOARDING_STATUS.APPROVED,
        color: "#1E8449",
        icon: ShieldCheck,
      },
      {
        label: "Rejected",
        key: ONBOARDING_STATUS.REJECTED,
        color: "#D64545",
        icon: XCircle,
      },
      // {
      //   label: "Deleted",
      //   key: ONBOARDING_STATUS.DELETED,
      //   color: "#B23A3A",
      //   icon: Trash2,
      // },
    ],
  },
  apiParams: {
    status: "status",
    financierType: "financierType",
    prn: "prn",
  },
  statusOptions: STATUS_OPTIONS,
  urlParams: ["status", "financierType", "prn"],
  dataField: "prn",
};

export const ONBOARDING_CONFIG = {
  id: TABLE_TYPES.ONBOARDED,
  title: "Onboarded",
  apiEndpoint: endpoints.getOnboardingDriver,
  columns: [
    { id: "prn", label: "PRN", width: "120px", whiteSpace: "nowrap" },
    {
      id: "moveOrderNumber",
      label: "Move Order No.",
      width: "120px",
      whiteSpace: "nowrap",
    },
    { id: "driverId", label: "DriverId", width: "100px", whiteSpace: "nowrap" },
    { id: "driverName", label: "Driver Name", width: "130px" },
    { id: "mobileNumber", label: "Phone Number", width: "130px" },
    { id: "financierType", label: "Financier", width: "130px" },
    { id: "status", label: "Status", width: "120px" },
    { id: "createdAt", label: "Date", width: "120px", whiteSpace: "nowrap" },
    { id: "action", label: "Action", width: "70px", align: "center" },
  ],
  filters: [
    {
      id: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Pending", value: "Pending" },
        { label: "Completed", value: "Completed" },
      ],
    },
    {
      id: "financierType",
      label: "Financier Type",
      type: "select",
      options: financierOptions,
    },
  ],
  stats: {
    type: "flat",
    source: "statusCounts",
    items: [
      // { label: "Completed", key: "Completed", color: "#0C7A45" },
      // { label: "Pending", key: "Pending", color: "#ECB65F" },
    ],
  },
  statusOptions: [
    { label: "Completed", value: "Completed" },
    { label: "Pending", value: "Pending" },
  ],
  apiParams: {
    status: "status",
    financierType: "financierType",
    prn: "prn",
  },
  urlParams: ["status", "financierType", "prn"],
  dataField: "prn",
};

export const LEADS_CONFIG = {
  id: TABLE_TYPES.LEADS,
  title: "Leads",
  apiEndpoint: endpoints.getDriverLeads,
  columns: [
    { id: "leadId", label: "Lead ID", width: "130px", whiteSpace: "nowrap" },
    {
      id: "dealerId",
      label: "Dealer Id",
      width: "120px",
      whiteSpace: "nowrap",
    },
    {
      id: "driverName",
      label: "Driver Name",
      width: "150px",
      whiteSpace: "nowrap",
    },
    {
      id: "driverId",
      label: "Driver ID",
      width: "150px",
      whiteSpace: "nowrap",
    },
    { id: "phoneNumber", label: "Phone Number", width: "130px" },
    { id: "financierType", label: "Financier", width: "130px" },
    // {
    //   id: "eVehicleNumber",
    //   label: "E-Vehicle Number",
    //   whiteSpace: "nowrap",
    //   width: "160px",
    // },
    { id: "status", label: "Status", width: "120px" },
    {
      id: "onboardingStatus",
      label: "Onboarding Status",
      whiteSpace: "nowrap",
      width: "180px",
    },
    {
      id: "createdAt",
      label: "Created Date",
      width: "120px",
      whiteSpace: "nowrap",
    },
    { id: "action", label: "Action", width: "70px", align: "center" },
  ],
  filters: [
    {
      id: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Approved", value: "APPROVED" },
        { label: "Soft Approved", value: "SOFT_APPROVED" },
        { label: "Draft", value: "DRAFT" },
        { label: "Rejected", value: "REJECTED" },
      ],
    },
    {
      id: "onboardingStatus",
      label: "Onboarding Status",
      type: "select",
      options: [
        { label: "Pending", value: ONBOARDING_STATUS.PENDING },
        { label: "On Hold", value: ONBOARDING_STATUS.HOLD },
        {
          label: "Changes Requested",
          value: ONBOARDING_STATUS.DOC_CHANGE_REQUESTED,
        },
        { label: "Approved", value: ONBOARDING_STATUS.APPROVED },
        { label: "Rejected", value: ONBOARDING_STATUS.REJECTED },
      ],
    },
    {
      id: "financierType",
      label: "Financier Type",
      type: "select",
      options: financierOptions,
    },
  ],

  stats: {
    type: "grouped",
    groups: [
      {
        title: "Lead status",
        source: "statusCounts",
        items: [
          {
            label: "Approved",
            key: "APPROVED",
            color: "#22A35F",
            icon: CheckCircle2,
          },
          {
            label: "Soft Approved",
            key: "SOFT_APPROVED",
            color: "#1e7949",
            icon: CheckCircle2,
          },
          { label: "Drafts", key: "DRAFT", color: "#A3B816", icon: FileEdit },
          {
            label: "Rejected",
            key: ONBOARDING_STATUS.REJECTED,
            color: "#B3261E",
            icon: XCircle,
          },
        ],
      },
      {
        title: "Onboarding status",
        source: "onboardingStatusCounts",
        items: [
          {
            label: "Pending",
            key: ONBOARDING_STATUS.PENDING,
            color: "#98A6A0",
            icon: Clock,
          },
          {
            label: "On Hold",
            key: ONBOARDING_STATUS.HOLD,
            color: "#ECB65F",
            icon: PauseCircle,
          },
          {
            label: "Change Req",
            key: ONBOARDING_STATUS.DOC_CHANGE_REQUESTED,
            color: "#D97706",
            icon: Edit3,
            urgent: true,
          },
          {
            label: "Approved",
            key: ONBOARDING_STATUS.APPROVED,
            color: "#0C7A45",
            icon: ShieldCheck,
          },
          {
            label: "Rejected",
            key: ONBOARDING_STATUS.REJECTED,
            color: "#B3261E",
            icon: XCircle,
          },
          // {
          //   label: "Deleted",
          //   key: ONBOARDING_STATUS.DELETED,
          //   color: "#fa1307",
          //   icon: Trash2,
          // },
        ],
      },
    ],
  },

  statusOptions: [
    { label: "Approved", value: ONBOARDING_STATUS.APPROVED },
    { label: "Rejected", value: ONBOARDING_STATUS.REJECTED },
    { label: "On Hold", value: ONBOARDING_STATUS.HOLD },
  ],

  apiParams: {
    status: "status",
    financierType: "financierType",
    leadId: "leadId",
  },
  urlParams: ["status", "financierType", "leadId"],
  dataField: "leadId",
};

export const getTableConfig = (tableType) => {
  switch (tableType) {
    case TABLE_TYPES.ONBOARDED:
      return ONBOARDING_CONFIG;
      break;
    case TABLE_TYPES.PRE_ONBOARDING:
      return PRE_ONBOARDING_CONFIG;
      break;
    case TABLE_TYPES.LEADS:
      return LEADS_CONFIG;
      break;

    default:
      break;
  }
};

export const STATUS_COLOR_MAP = {
  Active: "success",
  Inactive: "error",
  Pending: "warning",
  Completed: "success",
  InProgress: "info",
  Approved: "success",
  Rejected: "error",
  Deleted: "error",
};

// export const getFinancierColor = (type) => {
//   switch (type) {
//     case "LTO":
//       return "#1976d2";
//     case "AMU":
//       return "#2e7d32";
//     case "MOOVING":
//       return "#d32f2f";
//     default:
//       return "#757575";
//   }
// };

export const DEFAULT_PAGINATION = {
  limit: 10,
  offset: 0,
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
};

export const FIELD_EXTRACTORS = {
  driverName: (item) => {
    return item.driverDetails?.driverName || item.driverName || "--";
  },
  driverId: (item) => {
    return item.driverDetails?.driverId || item.driverId || "--";
  },
  mobileNumber: (item) => {
    return item.driverDetails?.mobileNumber || item?.mobileNumber || "--";
  },
  harness: (item) => {
    return item.harness || item?.harnessType || "--";
  },
  financierType: (item) => {
    return (
      item?.loanSummary?.financier ||
      item?.financierType ||
      item?.loanSummary?.financier ||
      "--"
    );
  },
  createdAt: (item) => {
    return (
      getFirebaseTimestampString(item.createdAt) ||
      getFirebaseTimestampString(item.date) ||
      "--"
    );
  },
};

export const STATUS_UPDATE_CONFIG = {
  [TABLE_TYPES.PRE_ONBOARDING]: {
    endpoint: endpoints.updatePreOnboardingDriverStatus,
    buildPayload: ({ status, updatedBy }) => ({ status, updatedBy }),
  },

  [TABLE_TYPES.LEADS]: {
    endpoint: endpoints.updateDriverLead,
    buildPayload: ({ status, updatedBy }) => ({ status, updatedBy }),
  },
};

export const ONBOARDING_STATUS_COLOR_MAP = {
  [ONBOARDING_STATUS.NOT_STARTED]: "default",
  [ONBOARDING_STATUS.IN_PROGRESS]: "warning",
  [ONBOARDING_STATUS.CHANGES_REQUESTED]: "urgent",
  [ONBOARDING_STATUS.APPROVED]: "success",
  [ONBOARDING_STATUS.REJECTED]: "error",
  [ONBOARDING_STATUS.COMPLETED]: "teal",
};

const FINANCIER_COLOR_MAP = {
  "IN-HOUSE": "teal",
  BANK: "success",
  NBFC: "warning",
  OTHER: "default",
};

export const getFinancierColor = (financierType) =>
  FINANCIER_COLOR_MAP[financierType] || "default";